import React from "react";
import { AITextEnhancerServiceProps, AITextEnhancerService, LastActionObj } from "./type";
import { TextEnhancementFormField } from "./enum";
import { getAITextActions, getInvalidTextToDisplay } from "./utils";
import { useAITextEnhancement } from "./dataloader";
import * as Yup from "yup";
import Validation from "shared/form/Validation";
import { useCopies } from "shared/hooks/useCopies";
import TextEnhancementType from "shared/enums/TextEnhancementType";
import EnhancementFieldType from "shared/enums/EnhancementFieldType";
import TextEnhancementRequest from "shared/types/ai/TextEnhancementRequest";
import { FormikProps, FormikValues } from "formik";

export default function useAIEnhancerService(props: AITextEnhancerServiceProps): AITextEnhancerService {
  const copies = useCopies();
  const [displayModal, setDisplayModal] = React.useState<boolean>(false);
  const [lastActionObj, setLastActionObj] = React.useState<LastActionObj | undefined>();
  const [invalidText, setInvalidText] = React.useState<string | undefined>();
  const formRef = React.useRef<FormikProps<any>>(null);
  const { isLoading: isGeneratingText, mutateAsync: generateText } = useAITextEnhancement();

  const toggleDisplayModal = React.useCallback(() => {
    if (displayModal) {
      setLastActionObj(undefined);
    }
    setDisplayModal(prev => !prev);
  }, [displayModal]);

  const aiTextActions = React.useMemo(() => getAITextActions(props.enhancementFieldType), [props.enhancementFieldType]);

  const generateTextActionHandler = React.useCallback(
    async (action?: TextEnhancementType) => {
      try {
        // undefined action means user has clicked on Recreate in which case we want to retrigger the last action
        const enhancementType = action ?? (lastActionObj?.action as TextEnhancementType);
        let req: TextEnhancementRequest = {
          registrationId: props.registrationId,
          enhancementType,
          text: props.value ?? "",
          minLength: props.minLength,
          maxLength: props.maxLength,
          enhancementFieldType: props.enhancementFieldType ?? EnhancementFieldType.SCHEMA_FIELD,
          // latest text that is available - the result we received from AI could have been edited by user
          previousText: formRef.current?.values[TextEnhancementFormField.ENHANCED_TEXT],
          previousEnhancementType: lastActionObj?.action
        };
        const resp = await generateText(req);
        if (resp.failureReason) {
          toggleDisplayModal();
          const invalidTextToDisplayCopyKey = getInvalidTextToDisplay(resp.failureReason);
          setInvalidText(copies.get(invalidTextToDisplayCopyKey));
        } else {
          setLastActionObj({ text: resp.text, action: enhancementType });
        }
      } catch (e) {}
    },
    [
      copies,
      generateText,
      lastActionObj?.action,
      props.enhancementFieldType,
      props.maxLength,
      props.minLength,
      props.registrationId,
      props.value,
      toggleDisplayModal
    ]
  );

  const initialValues = React.useMemo(() => {
    return { [TextEnhancementFormField.ENHANCED_TEXT]: lastActionObj?.text ?? "" };
  }, [lastActionObj?.text]);

  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      [TextEnhancementFormField.ENHANCED_TEXT]: Validation.textAreaValidation({
        required: true,
        min: props.minLength,
        max: props.maxLength,
        copies
      })
    });
  }, [copies, props.maxLength, props.minLength]);

  const submitHandler = React.useCallback(
    (values: FormikValues) => {
      props.insertClickHandler(values[TextEnhancementFormField.ENHANCED_TEXT] ?? "");
      toggleDisplayModal();
    },
    [props, toggleDisplayModal]
  );

  return {
    displayModal,
    toggleDisplayModal,
    aiTextActions,
    isGeneratingText,
    generateTextActionHandler,
    generatedText: lastActionObj?.text,
    lastActionTaken: lastActionObj?.action,
    initialValues,
    validationSchema,
    submitHandler,
    formRef,
    invalidText,
    setInvalidText
  };
}
